import { FileDiff, Gauge, Boxes } from "lucide-react";
import { Callout, Tag } from "@/components/GuidesShared";
import { StepList, BulletList } from "@/lib/guides/primitives";
import type { GuideSectionDef } from "@/lib/guides/types";

export const newPaidToolSections: GuideSectionDef[] = [
  {
    id: "handling-diff",
    label: "Handling Diff",
    icon: <FileDiff className="h-5 w-5" />,
    shortDescription: "Compare two handling.meta files as physics, not as text.",
    seoTitle: "Handling Diff — Compare FiveM handling.meta Files",
    seoDescription:
      "Compare two handling.meta files field by field. Float noise is ignored, flag bits and sub-handling are counted, and misspelled fields are listed instead of hidden.",
    keywords: [
      "handling.meta diff",
      "compare handling.meta",
      "FiveM handling compare",
      "GTA5 handling editor",
      "handling.meta checker",
    ],
    toolHref: "/handling-diff",
    faqs: [
      {
        q: "How is this different from a text diff?",
        a: "It reads each handling.meta as vehicle physics. Reordered lines and float noise like 1.0 versus 1.000000 are not reported as changes, while a changed flag bit or sub-handling value is.",
      },
      {
        q: "What if the files hold several vehicles?",
        a: "Vehicles are paired by handlingName, and a badge shows whether the pairing was automatic or one you chose. If the two names differ, the page says so above the rows.",
      },
      {
        q: "What does 'not recognised' mean?",
        a: "Any element in either file that no handling field claims, such as a misspelled tag. The game ignores these silently, so the tool lists them rather than showing a default.",
      },
      {
        q: "Is my handling.meta uploaded anywhere?",
        a: "No. Both sides are read and compared in your browser tab.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Handling Diff compares two <Tag>handling.meta</Tag> documents as physics records. Use
          it to see exactly what an edit, a vendor update or a tuning pass changed in a vehicle,
          without wading through reordered lines and rounding differences. It is included with the
          Vehicle Developer, Pro, and Studio plans.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            "Load side A, the file you started from, and side B, the edited one. Drop a file or paste the XML into either side.",
            "If a file holds several vehicles, check the match badge. Entries are paired by handlingName, and you can pick a different entry on either side.",
            "Read the headline count, which is split into fields, flags and sub-handling, then the rows grouped by category. Sort by biggest relative move if you prefer.",
            "Check the list of unrecognised elements for typos, then use Copy as Markdown to paste the result into a ticket or changelog.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">What it reports</p>
        <BulletList
          items={[
            "Changed fields, only where the difference clears that field's threshold. Smaller differences are shown separately as below the change threshold while Changed only is on, which is the default.",
            "Flag bits, including a sub-handling block's own flags, counted in the headline.",
            "Vehicles that exist in only one of the two files.",
          ]}
        />
        <Callout type="tip">
          Always read the unrecognised list before trusting a clean result. A misspelled field
          does nothing in game, and a diff that skips it will tell you nothing changed.
        </Callout>
      </>
    ),
  },
  {
    id: "resmon-report",
    label: "Resmon Report",
    icon: <Gauge className="h-5 w-5" />,
    shortDescription: "Rank a resmon dump by per-frame CPU and compare before and after.",
    seoTitle: "Resmon Report — Analyse FiveM resmon Dumps",
    seoDescription:
      "Paste a resmon dump from the in-game overlay, txAdmin, console, CSV or JSON. Rank resources by per-frame CPU, flag the heavy ones, and compare two captures.",
    keywords: [
      "FiveM resmon analyzer",
      "resmon report",
      "FiveM performance",
      "txAdmin resmon",
      "FiveM resource CPU ms",
    ],
    toolHref: "/resmon",
    faqs: [
      {
        q: "Which resmon formats can I paste?",
        a: "The in-game overlay, a txAdmin table, a console dump, a CSV or a JSON export. You can paste the text or drop a .txt, .log, .csv or .json file.",
      },
      {
        q: "What counts as a heavy resource?",
        a: "Per-frame CPU is ranked worst first. A resource at 1 ms or more is marked as worth a look, and 2 ms or more as high. The figure is per frame, and the whole frame at 60 fps is about 16 ms shared by every resource.",
      },
      {
        q: "What happens to lines the tool cannot read?",
        a: "They are listed with their raw text and the reason they were skipped, not dropped. The sum of the rows is also checked against the total the dump printed, and a mismatch is shown as a warning.",
      },
      {
        q: "Does it upload my dump or keep it?",
        a: "Nothing is uploaded. The two capture slots are saved in your browser's local storage so a reload does not lose your baseline, unless A and B together run past about a million characters or the browser refuses storage. Then they are not kept, and the page says so.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Resmon Report turns a pasted resmon dump into a table ranked by per-frame CPU time, so
          you can see which resources are costing you and whether a change actually helped. It is included
          with the Server Developer, Pro, and Studio plans.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            "Capture a resmon dump on your server before you change anything and paste it into slot A.",
            "Make the change, capture again, and paste it into slot B.",
            "Read each slot's table, ranked worst first, with the severity bands marked. Check the row total against the dump's own total.",
            "With both slots filled, the difference A to B section under the two panels shows each resource's move between A and B. Export CSV from either slot or from the difference.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">What it checks</p>
        <BulletList
          items={[
            "Rows read against the total the dump printed, with a warning when they disagree.",
            "Every unreadable line, listed with its raw text.",
            "Resources matched between captures by name, ignoring capitalisation, with a change under 0.005 ms not counted as a change.",
          ]}
        />
        <Callout type="tip">
          Capture A and B under similar conditions, at a similar player count and after the
          same uptime. A heavy resource at 3 a.m. and the same resource at peak hours are not
          comparable numbers.
        </Callout>
      </>
    ),
  },
  {
    id: "asset-directory",
    label: "Asset Directory",
    icon: <Boxes className="h-5 w-5" />,
    shortDescription: "Browse vanilla GTA V vehicles, peds and clothing with their DLC pack.",
    seoTitle: "Asset Directory — Vanilla GTA V Vehicles, Peds and Clothing",
    seoDescription:
      "Search every vanilla GTA V vehicle, ped and freemode clothing item by spawn name, with DLC pack and release date, untextured 3D previews and, for vehicles and peds, a spawn snippet.",
    keywords: [
      "GTA V vehicle list",
      "GTA5 spawn names",
      "vanilla ped list",
      "GTA V DLC pack list",
      "FiveM asset directory",
    ],
    toolHref: "/assets",
    faqs: [
      {
        q: "What is in the directory?",
        a: "Vanilla GTA V vehicles, peds and freemode clothing, each with the DLC pack it ships in and a release date where one is known.",
      },
      {
        q: "How do I search?",
        a: "Type a spawn name or part of one; for clothing, type part of the Rockstar identifier, such as lts or uppr. Names are matched as typed, so a partial spawn name works but a display name does not. You can also narrow by group and by DLC pack.",
      },
      {
        q: "Are the 3D previews the real models?",
        a: "No. They are untextured grey geometry, decimated, and they only appear where one has been baked. Vehicle previews do not include wheels.",
      },
      {
        q: "Does a name check tell me a name is safe?",
        a: "No. The detail page can check a name against the vanilla names, but a miss only means no collision was found for the build the catalogue was baked from. A later DLC could still use it.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          The Asset Directory is a baked catalogue of what ships with GTA V. Use it to look up a
          spawn name, find which DLC pack an item came from, or check what already exists
          before you name your own asset. It is included with the Vehicle Developer, Server
          Developer, Pro, and Studio plans.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            "Pick Vehicles, Peds or Clothing. Each section loads the first time you open it.",
            "Search by spawn name, or by the Rockstar identifier for clothing, and narrow with the group and DLC pack selectors. A broad search shows the first slice and the total matched, so narrow it to reach the rest.",
            "Open an item for its detail page: DLC pack and release date. Vehicles and peds also have a spawn snippet you can copy; clothing pages show the identifier, packs and outfits instead.",
            "Use the name check on the detail page to test a name against the stock files. On a vehicle's page, Open in Easy Meta carries on into the meta tools.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Good to know</p>
        <BulletList
          items={[
            "The page prints the game build and bake date. Anything added by a newer DLC will not be listed.",
            "Open in UV Studio needs your own copy of the vehicle's .yft, because the preview is decimated and untextured.",
          ]}
        />
        <Callout type="tip">
          Treat a name check with no hit as a prompt to look further, not a clearance. It
          reports that no collision was found for this build, not that the name is free.
        </Callout>
      </>
    ),
  },
];
